import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { formatSiteMonthYear } from "@/lib/datetime.schedule";
import { ArrowRight } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BLOG_PAGE_SIZE, listPublishedBlogPosts } from "@/lib/blog.functions";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import { pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime";

export const Route = createFileRoute("/blog/")({
  head: () =>
    pageHead({
      title: m.blog_meta_title(),
      description: m.blog_meta_description(),
      path: "/blog",
    }),
  component: BlogPage,
});

type BlogListPost = Awaited<ReturnType<typeof listPublishedBlogPosts>>["posts"][number];

function BlogPostCard({ post }: { post: BlogListPost }) {
  return (
    <article className="border-l-2 border-ember pl-6 pb-10">
      <div className="font-display text-xs tracking-[0.18em] text-ember">
        {formatSiteMonthYear(post.published_at, getLocale())}
      </div>
      <h2 className="mt-2 font-display text-3xl">{post.title}</h2>
      {post.excerpt && <p className="mt-3 text-foreground/75">{post.excerpt}</p>}
      <Link
        to="/blog/$slug"
        params={{ slug: post.slug }}
        className="mt-4 inline-flex items-center gap-2 font-display text-sm tracking-[0.12em] text-ember hover:text-ember/80"
      >
        {m.blog_read_more()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
      </Link>
    </article>
  );
}

function BlogPage() {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const postsQ = useInfiniteQuery({
    queryKey: ["blog-posts"],
    queryFn: ({ pageParam }) =>
      listPublishedBlogPosts({ data: { page: pageParam, pageSize: BLOG_PAGE_SIZE } }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + 1 : undefined,
  });

  const posts = postsQ.data?.pages.flatMap((page) => page.posts) ?? [];
  const rowCount = posts.length + (postsQ.hasNextPage ? 1 : 0);

  useLayoutEffect(() => {
    const update = () => setScrollMargin(listRef.current?.offsetTop ?? 0);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => 220,
    overscan: 2,
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems.at(-1);
    if (!lastItem) return;
    if (lastItem.index >= posts.length - 1 && postsQ.hasNextPage && !postsQ.isFetchingNextPage) {
      void postsQ.fetchNextPage();
    }
  }, [virtualItems, posts.length, postsQ]);

  return (
    <>
      <section className="bg-ink">
        <div className="container-page page-hero-pad">
          <span className="eyebrow">{m.nav_blog()}</span>
          <h1 className="mt-3 font-display text-hero leading-[0.95] md:text-8xl">
            <EmphasizedTitle lead={m.blog_title_lead()} accent={m.blog_title_accent()} />
          </h1>
        </div>
      </section>

      <section className="container-page page-follow-pad">
        <div ref={listRef} className="relative mx-auto max-w-3xl">
          {postsQ.isLoading && <p className="text-foreground/60">{m.blog_loading()}</p>}

          {postsQ.isError && <p className="text-foreground/60">{m.blog_load_error()}</p>}

          {!postsQ.isLoading && !postsQ.isError && posts.length === 0 && (
            <p className="text-foreground/60">{m.blog_empty()}</p>
          )}

          {posts.length > 0 && (
            <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
              {virtualItems.map((virtualRow) => {
                const isLoaderRow = virtualRow.index >= posts.length;
                const post = posts[virtualRow.index];

                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    className="absolute left-0 top-0 w-full"
                    style={{
                      transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                    }}
                  >
                    {isLoaderRow ? (
                      <p className="py-4 text-sm text-foreground/60">
                        {postsQ.isFetchingNextPage ? m.blog_more_posts() : ""}
                      </p>
                    ) : (
                      <BlogPostCard post={post} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
